import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Fab, Fade } from '@mui/material';
import _ from 'lodash';
import { useState, useEffect, useCallback } from 'react';

const SCROLL_THRESHOLD = 300;
const FADE_TIMEOUT = 600;

/**
 * Renders a button that allows users to smoothly scroll back to the top of the page.
 * The button becomes visible once the user has scrolled down past a specific threshold.
 * It employs a fade transition for both appearing and disappearing based on the user's scroll position.
 *
 * Features include:
 * - Throttling on the scroll event handler to improve performance.
 * - Conditional rendering based on the scroll position, with a timeout to delay hiding for fade-out animation.
 * - Optional `onClick` handler that can be triggered when the button is clicked.
 *
 * @param props The properties passed to the component. - Optional click handler invoked when the button is clicked.
 *
 * @returns The button component wrapped in a fade transition, or null if not visible.
 */
export function BackToTop({
  onClick,
}: {
  onClick?: () => void;
}): JSX.Element | null {
  const [isAboveThreshold, setIsAboveThreshold] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(false);

  const handleScroll = useCallback(
    _.throttle(() => {
      const scrolled = window.pageYOffset || document.documentElement.scrollTop;
      setIsAboveThreshold(scrolled > SCROLL_THRESHOLD);
    }, 100),
    []
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (isAboveThreshold) {
      setIsButtonVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsButtonVisible(false);
      }, FADE_TIMEOUT);
      return () => clearTimeout(timer);
    }
  }, [isAboveThreshold]);

  const handleButtonClick = useCallback(() => {
    if (onClick) onClick();
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, [onClick]);

  return isButtonVisible ? (
    <Fade
      in={isAboveThreshold}
      timeout={FADE_TIMEOUT}
      easing={{ enter: 'ease-in-out', exit: 'ease-in-out' }}
      unmountOnExit
    >
      <Fab
        data-test-id="back-to-top-selector"
        color="primary"
        size="small"
        onClick={handleButtonClick}
        sx={(theme) => ({
          background: theme.colors.grey[800],
          bottom: 'calc(152px + 24px)', // footer is 152px high
          position: 'fixed',
          right: '24px',
        })}
        aria-label="Back to top"
      >
        <ExpandLessIcon />
      </Fab>
    </Fade>
  ) : null;
}
